import { Box, Card, Divider, Grid, TextField, Typography, CircularProgress, Fade, Grow } from '@mui/material'
import { useRef, useEffect, useState } from 'react'
import { CardViewTicket } from '../../../components'
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TicketsList = ({
    tickets,
    ticketsDisplayed,
    totalElements,
    totalPages,
    currentPage,
    isLoading,
    isInitialLoad,
    displayCount,
    setDisplayCount,
    fetchTickets,
    textSearch,
    setTextSearch,
    styleInput,
    isDarkMode,
}) => {
    const observerTarget = useRef(null);

    useEffect(() => {
        const currentTarget = observerTarget.current;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    if (displayCount < tickets.length) {
                        setDisplayCount(prev => Math.min(prev + 20, tickets.length));
                    } else if (currentPage + 1 < totalPages && !isLoading) {
                        fetchTickets(currentPage + 1);
                    }
                }
            },
            { threshold: 0.1, rootMargin: '200px' }
        );
        if (currentTarget) observer.observe(currentTarget);
        return () => {
            if (currentTarget) observer.unobserve(currentTarget);
        };
    }, [displayCount, tickets.length, currentPage, totalPages, isLoading, fetchTickets, setDisplayCount]);

    return (
        <Grid item xs={12} mt={2} sx={{ marginBottom: 15 }}>
            <Card sx={{
                background: isDarkMode ? '#1F2A40' : '#fff',
                borderRadius: '16px',
                boxShadow: isDarkMode
                    ? '0 4px 20px rgba(0,0,0,0.3)'
                    : '0 4px 20px rgba(0,0,0,0.06)',
                border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e0e0e0'
            }}>
                <Grid container>
                    <Grid item xs={10} sm={10} p={1}>
                        <TextField
                            focused
                            value={textSearch}
                            autoComplete='off'
                            placeholder="Buscar por ID o título..."
                            onChange={(e) => setTextSearch(e.target.value)}
                            InputProps={{
                                type: 'search',
                                startAdornment: (
                                    <SearchIcon sx={{ color: isDarkMode ? '#b0bec5' : '#2c3e50' }} size={20} />
                                ),
                            }}
                            sx={styleInput}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} display='flex' alignItems='center' justifyContent='center' p={1}>
                        <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 'bold' }}>
                            {`${totalElements || '0'} resultados`}
                        </Typography>
                    </Grid>
                </Grid>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : '#e0e0e0' }} />
                <Grid container>
                    <Grid item xs={8} sm={10} p={1.5}>
                        <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 'bold', ml: 10 }}>Ticket</Typography>
                    </Grid>
                    <Grid item xs={2} sm={2} display='flex' alignItems='center'>
                        <Divider orientation='vertical' sx={{ background: isDarkMode ? '#3d4a62' : '#e0e0e0' }} />
                        <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 'bold', textAlign: 'center', ml: 4 }}>Estado</Typography>
                    </Grid>
                </Grid>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : '#e0e0e0' }} />
                <Box mb={5}>
                    {(isLoading && isInitialLoad) ? (
                        <Fade in={true} timeout={600}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '300px',
                                flexDirection: 'column',
                                gap: 3,
                                position: 'relative',
                            }}>
                                <Box sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                    background: isDarkMode
                                        ? 'linear-gradient(135deg, #1F2A40 0%, #2d3a52 100%)'
                                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    borderRadius: '20px',
                                    padding: '40px 50px',
                                    boxShadow: isDarkMode
                                        ? '0 10px 40px rgba(0,0,0,0.4)'
                                        : '0 10px 40px rgba(0,0,0,0.08)',
                                    animation: 'float 3s ease-in-out infinite',
                                    '@keyframes float': {
                                        '0%, 100%': { transform: 'translateY(0px)' },
                                        '50%': { transform: 'translateY(-10px)' }
                                    }
                                }}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress size={70} thickness={3} sx={{ color: '#0364ff' }} />
                                    </Box>
                                    <Typography sx={{
                                        color: isDarkMode ? '#e0e0e0' : '#2c3e50',
                                        fontSize: 18,
                                        fontWeight: 700,
                                        textAlign: 'center'
                                    }}>
                                        Cargando tickets
                                    </Typography>
                                    <Typography sx={{
                                        color: isDarkMode ? '#b0bec5' : '#7f8c8d',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        textAlign: 'center',
                                    }}>
                                        Por favor espera...
                                    </Typography>
                                </Box>
                            </Box>
                        </Fade>
                    ) : tickets?.length > 0 ? (
                        <>
                            {ticketsDisplayed.map((tkv, index) => (
                                <Fade
                                    key={tkv?.id_uxt}
                                    in={true}
                                    timeout={300}
                                    style={{ transitionDelay: `${Math.min(index * 30, 300)}ms` }}
                                >
                                    <Box>
                                        <CardViewTicket item={tkv} isDarkMode={isDarkMode} textSearch={textSearch} />
                                    </Box>
                                </Fade>
                            ))}
                            {(displayCount < tickets.length || (currentPage + 1 < totalPages)) && (
                                <Grow in={true} timeout={500}>
                                    <Box
                                        ref={observerTarget}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            py: 4,
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '80%',
                                                height: '1px',
                                                background: isDarkMode
                                                    ? 'linear-gradient(90deg, transparent, #3d4a62 50%, transparent)'
                                                    : 'linear-gradient(90deg, transparent, #e0e0e0 50%, transparent)',
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            background: isDarkMode
                                                ? 'linear-gradient(135deg, #1F2A40 0%, #2d3a52 100%)'
                                                : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                            borderRadius: '50px',
                                            px: 3,
                                            py: 1.5,
                                            boxShadow: isDarkMode
                                                ? '0 4px 12px rgba(0,0,0,0.3)'
                                                : '0 4px 12px rgba(0,0,0,0.08)',
                                            animation: 'pulse 2s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%, 100%': { transform: 'scale(1)' },
                                                '50%': { transform: 'scale(1.02)' }
                                            }
                                        }}>
                                            <CircularProgress size={24} thickness={4} sx={{ color: '#0364ff' }} />
                                            <Box>
                                                <Typography sx={{
                                                    color: isDarkMode ? '#e0e0e0' : '#2c3e50',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                }}>
                                                    Cargando más tickets...
                                                </Typography>
                                                <Typography sx={{
                                                    color: isDarkMode ? '#b0bec5' : '#7f8c8d',
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    mt: 0.3
                                                }}>
                                                    {ticketsDisplayed.length} de {totalElements} mostrados
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            mt: 2,
                                            width: '200px',
                                            height: '4px',
                                            background: isDarkMode ? '#3d4a62' : '#e0e0e0',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            position: 'relative'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                height: '100%',
                                                width: `${(tickets.length / totalElements) * 100}%`,
                                                background: 'linear-gradient(90deg, #0364ff 0%, #4a90e2 100%)',
                                                borderRadius: '10px',
                                                transition: 'width 0.5s ease',
                                                boxShadow: '0 0 10px rgba(3, 100, 255, 0.3)'
                                            }} />
                                        </Box>
                                        <Box sx={{
                                            mt: 2,
                                            animation: 'bounce 2s ease-in-out infinite',
                                            '@keyframes bounce': {
                                                '0%, 100%': { transform: 'translateY(0px)' },
                                                '50%': { transform: 'translateY(-10px)' }
                                            }
                                        }}>
                                            <ExpandMoreIcon sx={{ fontSize: 28, color: '#0364ff', opacity: 0.6 }} />
                                        </Box>
                                    </Box>
                                </Grow>
                            )}
                            {displayCount >= tickets.length && currentPage + 1 >= totalPages && tickets.length > 0 && (
                                <Fade in={true} timeout={800}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 3,
                                        gap: 1
                                    }}>
                                        <Box sx={{
                                            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                                            borderRadius: '25px',
                                            px: 3,
                                            py: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                                            border: '1px solid #a5d6a7'
                                        }}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                background: '#4caf50',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                                <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{
                                                    color: '#2e7d32',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                }}>
                                                    Todos los tickets cargados
                                                </Typography>
                                                <Typography sx={{
                                                    color: '#558b2f',
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    mt: 0.2
                                                }}>
                                                    {totalElements} tickets en total
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Fade>
                            )}
                        </>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '300px',
                            flexDirection: 'column'
                        }}>
                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 18, fontWeight: 'bold' }}>
                                No se encontraron tickets
                            </Typography>
                            <Typography sx={{ color: isDarkMode ? '#7f8c8d' : '#9ca3af', fontSize: 14, mt: 1 }}>
                                Intenta cambiar los filtros de búsqueda
                            </Typography>
                        </Box>
                    )}
                    {isLoading && !isInitialLoad && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={30} sx={{ color: '#0364ff' }} />
                        </Box>
                    )}
                </Box>
            </Card>
        </Grid>
    );
};

export default TicketsList;
